import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Copy, 
  Check, 
  Download,
  Share2,
  Coins,
  Users,
  Globe,
  TrendingUp,
  Shield,
  Sparkles,
  Heart,
  Star,
  Zap,
  Target,
  Gift,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

// Social media icons
const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

const XIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const YouTubeIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

export default function SocialMediaAdKit() {
  const [copiedId, setCopiedId] = useState(null);
  const [selectedAd, setSelectedAd] = useState(0);
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://susumoney.com';

  const adCreatives = [
    {
      id: 'main',
      name: 'Main Campaign',
      tagline: 'Save Together, Grow Together',
      headline: 'Join the Global Susu Movement',
      description: 'Start saving with as little as $0.10. Join 5,000+ savers across 50+ countries building wealth through community savings.',
      cta: 'Start Saving FREE',
      gradient: 'from-indigo-600 via-purple-600 to-pink-500',
      hashtags: '#SusuMoney #CommunitySavings #FinancialFreedom #SaveTogether #AfricanFintech #MoneyGoals',
      emoji: '💰'
    },
    {
      id: 'youth',
      name: 'Youth Focus',
      tagline: 'Your Money, Your Future',
      headline: 'Savings Made Fun & Social',
      description: 'Why save alone when you can save with friends? Join the coolest savings app and watch your money grow! 🚀',
      cta: 'Join the Movement',
      gradient: 'from-amber-500 via-orange-500 to-red-500',
      hashtags: '#GenZFinance #YoungSavers #MoneyTok #FinancialLiteracy #SavingsChallenge #MoneyMoves',
      emoji: '🚀'
    },
    {
      id: 'diaspora',
      name: 'Diaspora',
      tagline: 'Stay Connected to Your Roots',
      headline: 'Susu Goes Global',
      description: 'The traditional African savings circle, reimagined for the digital age. Save with family & friends anywhere in the world.',
      cta: 'Connect & Save',
      gradient: 'from-green-500 via-emerald-500 to-teal-500',
      hashtags: '#AfricanDiaspora #RemittanceAlternative #FamilySavings #CultureMeetsInnovation #GlobalAfrican',
      emoji: '🌍'
    },
    {
      id: 'security',
      name: 'Trust & Security',
      tagline: '100% Secure Savings',
      headline: 'Your Money is Protected',
      description: 'Bank-level security with Stripe payments. Protection deposits ensure everyone plays fair. Save with confidence.',
      cta: 'Save Securely',
      gradient: 'from-blue-600 via-cyan-500 to-teal-400',
      hashtags: '#SecureSavings #TrustedPlatform #ProtectedMoney #SafeInvesting #FinancialSecurity',
      emoji: '🔒'
    }
  ];

  const currentAd = adCreatives[selectedAd];

  const socialCaptions = {
    facebook: `${currentAd.emoji} ${currentAd.tagline}!\n\n${currentAd.description}\n\n✨ ${currentAd.cta}: ${baseUrl}\n\n${currentAd.hashtags}`,
    instagram: `${currentAd.emoji} ${currentAd.headline}\n\n${currentAd.description}\n\n👆 Link in bio to start saving!\n\n${currentAd.hashtags}`,
    tiktok: `${currentAd.emoji} ${currentAd.tagline} ${currentAd.hashtags.split(' ').slice(0, 5).join(' ')}`,
    twitter: `${currentAd.emoji} ${currentAd.tagline}\n\n${currentAd.description.substring(0, 180)}...\n\n${currentAd.cta} 👉 ${baseUrl}\n\n${currentAd.hashtags.split(' ').slice(0, 4).join(' ')}`,
    whatsapp: `${currentAd.emoji} *${currentAd.headline}*\n\n${currentAd.description}\n\n✨ ${currentAd.cta}: ${baseUrl}`,
    linkedin: `${currentAd.headline}\n\n${currentAd.description}\n\nSusu Money is revolutionizing community savings with a secure, digital platform that honors traditional African savings practices while embracing modern technology.\n\n🔗 Learn more: ${baseUrl}\n\n${currentAd.hashtags}`,
    youtube: `${currentAd.emoji} ${currentAd.headline} | ${currentAd.tagline}\n\n${currentAd.description}\n\n🔔 Subscribe for more financial tips!\n\n${currentAd.cta}: ${baseUrl}\n\n${currentAd.hashtags}`
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedId(null), 3000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const shareToSocial = (platform) => {
    const text = encodeURIComponent(socialCaptions[platform] || socialCaptions.facebook);
    const url = encodeURIComponent(baseUrl);
    
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}`,
      whatsapp: `https://api.whatsapp.com/send?text=${text}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
    };

    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=500');
    } else {
      copyToClipboard(socialCaptions[platform], platform);
      toast.success(`Caption copied! Paste it on ${platform}`);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 text-white py-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="bg-indigo-500/20 text-indigo-300 mb-4">
            <Sparkles className="w-4 h-4 mr-1" />
            Social Media Ad Kit
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Spread the Word About Susu Money
          </h1>
          <p className="text-xl text-stone-300 max-w-2xl mx-auto">
            Ready-to-use ads, captions, and creatives for all social media platforms. 
            Help grow our community and share the joy of saving together!
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Ad Style Selector */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-stone-900 mb-4">Choose Ad Style</h2>
          <div className="flex flex-wrap gap-3">
            {adCreatives.map((ad, index) => (
              <button
                key={ad.id}
                onClick={() => setSelectedAd(index)}
                className={`px-4 py-2 rounded-xl font-medium transition-all ${
                  selectedAd === index
                    ? `bg-gradient-to-r ${ad.gradient} text-white shadow-lg`
                    : 'bg-white border border-stone-200 text-stone-700 hover:border-stone-300'
                }`}
                data-testid={`ad-style-${ad.id}`}
              >
                {ad.emoji} {ad.name}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Ad Preview */}
          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-4">Ad Preview</h2>
            <motion.div
              key={selectedAd}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`bg-gradient-to-br ${currentAd.gradient} rounded-3xl p-8 text-white shadow-2xl`}
            >
              {/* Logo */}
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                  <Coins className="w-7 h-7" />
                </div>
                <div>
                  <span className="text-2xl font-bold">Susu Money</span>
                  <p className="text-white/70 text-sm">Global Savings Community</p>
                </div>
              </div>

              {/* Main Content */}
              <div className="mb-8">
                <p className="text-white/80 text-lg mb-2">{currentAd.tagline}</p>
                <h3 className="text-4xl font-bold mb-4">{currentAd.headline}</h3>
                <p className="text-white/90 text-lg">{currentAd.description}</p>
              </div>

              {/* Features */}
              <div className="grid grid-cols-2 gap-3 mb-8">
                {[
                  { icon: Users, text: '5,000+ Savers' },
                  { icon: Globe, text: '50+ Countries' },
                  { icon: TrendingUp, text: 'Earn Interest' },
                  { icon: Shield, text: '100% Secure' }
                ].map((feature, i) => (
                  <div key={i} className="bg-white/10 rounded-xl p-3 flex items-center">
                    <feature.icon className="w-5 h-5 mr-2" />
                    <span className="text-sm font-medium">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="bg-white rounded-2xl p-6 text-center">
                <p className="text-stone-900 font-bold text-xl mb-2">{currentAd.cta}</p>
                <p className="text-indigo-600 font-semibold">{baseUrl}</p>
                <p className="text-stone-500 text-sm mt-2">FREE to join • No hidden fees</p>
              </div>

              {/* Hashtags */}
              <p className="text-white/50 text-xs mt-6 text-center">
                {currentAd.hashtags}
              </p>
            </motion.div>
          </div>

          {/* Platform Captions */}
          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-4">Platform Captions</h2>
            <Tabs defaultValue="facebook" className="w-full">
              <TabsList className="grid grid-cols-4 lg:grid-cols-7 gap-1 mb-4 bg-stone-100 p-1 rounded-xl">
                {[
                  { id: 'facebook', icon: FacebookIcon, label: 'FB' },
                  { id: 'instagram', icon: InstagramIcon, label: 'IG' },
                  { id: 'tiktok', icon: TikTokIcon, label: 'TT' },
                  { id: 'twitter', icon: XIcon, label: 'X' },
                  { id: 'whatsapp', icon: WhatsAppIcon, label: 'WA' },
                  { id: 'linkedin', icon: LinkedInIcon, label: 'LI' },
                  { id: 'youtube', icon: YouTubeIcon, label: 'YT' }
                ].map((platform) => (
                  <TabsTrigger 
                    key={platform.id} 
                    value={platform.id}
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  >
                    <platform.icon />
                  </TabsTrigger>
                ))}
              </TabsList>

              {Object.entries(socialCaptions).map(([platform, caption]) => (
                <TabsContent key={platform} value={platform}>
                  <Card className="border-stone-200">
                    <CardContent className="p-4">
                      <div className="bg-stone-50 rounded-xl p-4 mb-4 max-h-64 overflow-y-auto">
                        <pre className="whitespace-pre-wrap text-sm text-stone-700 font-sans">
                          {caption}
                        </pre>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => copyToClipboard(caption, platform)}
                          variant="outline"
                          className="flex-1 rounded-xl"
                          data-testid={`copy-${platform}-caption`}
                        >
                          {copiedId === platform ? (
                            <>
                              <Check className="w-4 h-4 mr-2 text-green-600" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Caption
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => shareToSocial(platform)}
                          className="rounded-xl bg-indigo-600 hover:bg-indigo-700"
                          data-testid={`share-${platform}`}
                        >
                          <Share2 className="w-4 h-4 mr-2" />
                          Share
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>

            {/* Quick Share Buttons */}
            <div className="mt-6">
              <h3 className="font-semibold text-stone-900 mb-3">Quick Share</h3>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'facebook', icon: FacebookIcon, color: 'bg-[#1877F2]', label: 'Facebook' },
                  { id: 'twitter', icon: XIcon, color: 'bg-black', label: 'X' },
                  { id: 'whatsapp', icon: WhatsAppIcon, color: 'bg-[#25D366]', label: 'WhatsApp' },
                  { id: 'linkedin', icon: LinkedInIcon, color: 'bg-[#0A66C2]', label: 'LinkedIn' }
                ].map((platform) => (
                  <Button
                    key={platform.id}
                    onClick={() => shareToSocial(platform.id)}
                    className={`${platform.color} text-white rounded-xl hover:opacity-90`}
                    data-testid={`quick-share-${platform.id}`}
                  >
                    <platform.icon />
                    <span className="ml-2 hidden sm:inline">{platform.label}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Additional Ad Formats */}
        <section className="mt-16">
          <h2 className="text-2xl font-bold text-stone-900 mb-6">More Ad Formats</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Story Format */}
            <Card className="border-stone-200 overflow-hidden">
              <div className={`bg-gradient-to-b ${currentAd.gradient} p-6 text-white aspect-[9/16] flex flex-col justify-between`}>
                <div className="flex items-center">
                  <Coins className="w-6 h-6 mr-2" />
                  <span className="font-bold">Susu Money</span>
                </div>
                <div className="text-center">
                  <p className="text-6xl mb-4">{currentAd.emoji}</p>
                  <h3 className="text-2xl font-bold mb-2">{currentAd.tagline}</h3>
                  <p className="text-white/80 text-sm">{currentAd.description.substring(0, 80)}...</p>
                </div>
                <div className="bg-white/20 rounded-xl p-3 text-center">
                  <p className="font-bold">Swipe Up to Join!</p>
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-stone-600 mb-2">Instagram/TikTok Story (9:16)</p>
                <Button
                  onClick={() => copyToClipboard(socialCaptions.instagram, 'story')}
                  variant="outline"
                  size="sm"
                  className="w-full rounded-lg"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Story Caption
                </Button>
              </CardContent>
            </Card>

            {/* Square Format */}
            <Card className="border-stone-200 overflow-hidden">
              <div className={`bg-gradient-to-br ${currentAd.gradient} p-6 text-white aspect-square flex flex-col justify-center`}>
                <div className="text-center">
                  <Coins className="w-12 h-12 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold mb-2">{currentAd.headline}</h3>
                  <p className="text-white/80 text-sm mb-4">{currentAd.description.substring(0, 100)}...</p>
                  <div className="bg-white text-stone-900 rounded-xl px-4 py-2 inline-block font-bold">
                    {currentAd.cta} →
                  </div>
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-stone-600 mb-2">Instagram Feed (1:1)</p>
                <Button
                  onClick={() => copyToClipboard(socialCaptions.instagram, 'square')}
                  variant="outline"
                  size="sm"
                  className="w-full rounded-lg"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Feed Caption
                </Button>
              </CardContent>
            </Card>

            {/* Wide Format */}
            <Card className="border-stone-200 overflow-hidden">
              <div className={`bg-gradient-to-r ${currentAd.gradient} p-6 text-white aspect-video flex items-center`}>
                <div className="flex-1">
                  <p className="text-white/80 mb-1">{currentAd.tagline}</p>
                  <h3 className="text-xl font-bold mb-2">{currentAd.headline}</h3>
                  <div className="bg-white text-stone-900 rounded-lg px-3 py-1 inline-block text-sm font-bold">
                    {currentAd.cta}
                  </div>
                </div>
                <div className="w-20 h-20 bg-white/20 rounded-xl flex items-center justify-center">
                  <Coins className="w-10 h-10" />
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-stone-600 mb-2">Facebook/Twitter (16:9)</p>
                <Button
                  onClick={() => copyToClipboard(socialCaptions.facebook, 'wide')}
                  variant="outline"
                  size="sm"
                  className="w-full rounded-lg"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Facebook Caption
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Tips Section */}
        <section className="mt-16 bg-indigo-50 rounded-3xl p-8">
          <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">
            📱 Posting Tips for Maximum Engagement
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Target, title: 'Best Times', desc: 'Post between 6-9 PM local time for maximum reach' },
              { icon: Heart, title: 'Engage Back', desc: 'Reply to comments within 1 hour to boost visibility' },
              { icon: Star, title: 'Use Stories', desc: 'Stories get 2x more engagement than regular posts' },
              { icon: Zap, title: 'Be Consistent', desc: 'Post 3-5 times per week for best results' }
            ].map((tip, i) => (
              <div key={i} className="bg-white rounded-xl p-4 text-center">
                <tip.icon className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                <h3 className="font-semibold text-stone-900">{tip.title}</h3>
                <p className="text-sm text-stone-600">{tip.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
